import { fetchPromotionData } from "/app/src/routes/{-$locale}/tags/$id.tsx?tsr-shared=1";
const $$splitComponentImporter = () => import('/app/src/routes/{-$locale}/tags/$id.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
import { createFileRoute } from "@tanstack/react-router";
import { dehydrate } from "@tanstack/react-query";
import { z } from "zod";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { httpClient } from "@/lib/http-client";
import { getImage } from "@/lib/get-image";
import i18n from "@/lib/i18n";
type SortBy = "releaseDate" | "lastModifiedDate" | "effectiveDate" | "creationDate" | "viewableDate" | "pcReleaseDate" | "upcoming" | "price";
const searchParamsSchema = z.object({
  page: z.number().optional(),
  sortBy: z.enum(["releaseDate", "lastModifiedDate", "effectiveDate", "creationDate", "viewableDate", "pcReleaseDate", "upcoming", "price"]).optional(),
  sortDir: z.enum(["asc", "desc"]).optional(),
  q: z.string().optional(),
  country: z.string().optional()
});
export const Route = createFileRoute("/{-$locale}/tags/$id")({
  component: lazyRouteComponent($$splitComponentImporter, 'component'),
  // @ts-expect-error - loader return type
  loader: async ({
    context,
    params,
    search
  }) => {
    const {
      queryClient,
      country
    } = context;
    const {
      id
    } = params;

    // const { page, sortBy, sortDir, q } = search;
    const page = search?.page ?? 1;
    const sortBy = search?.sortBy ?? "lastModifiedDate";
    const sortDir = search?.sortDir ?? "desc";
    const q = search?.q ?? "";
    const [coverData, initialData] = await Promise.allSettled([queryClient.fetchQuery({
      queryKey: ["promotion-cover", {
        id
      }],
      queryFn: () => httpClient.get<Pick<SingleOffer, "_id" | "id" | "namespace" | "title" | "keyImages">>(`/promotions/${id}/cover`).catch(() => null)
    }), queryClient.fetchQuery({
      queryKey: ["promotion-meta", {
        id,
        country,
        limit: 20,
        sortBy,
        sortDir,
        query: q,
        page: 1
      }],
      queryFn: () => fetchPromotionData({
        id,
        country: country ?? "US",
        page: 1,
        sortBy,
        sortDir,
        query: q
      }).catch(() => null)
    }), queryClient.prefetchInfiniteQuery({
      queryKey: ["promotion", {
        id,
        country,
        sortBy,
        sortDir,
        query: q,
        limit: 20
      }],
      queryFn: ({
        pageParam
      }) => fetchPromotionData({
        id,
        country: country ?? "US",
        page: pageParam as number,
        sortBy,
        sortDir,
        query: q
      }).catch(() => null),
      initialPageParam: page,
      getNextPageParam: (lastPage: {
        elements: SingleOffer[];
        start: number;
        count: number;
      } | null, allPages: ({
        elements: SingleOffer[];
        start: number;
        count: number;
      } | null)[]) => {
        if (!lastPage) return undefined;
        // If the start is greater than the count, we have reached the end
        if (lastPage.start + 20 > lastPage.count) {
          return undefined;
        }
        return allPages?.length + 1;
      }
    })]);
    const cover = coverData.status === "fulfilled" ? coverData.value : null;
    return {
      cover,
      id,
      promotion: initialData.status === "fulfilled" ? initialData.value : null,
      dehydratedState: dehydrate(queryClient)
    };
  },
  validateSearch: zodSearchValidator(searchParamsSchema),
  head: ctx => {
    const {
      params,
      loaderData
    } = ctx;
    if (!loaderData) {
      return {
        meta: [{
          title: i18n.t("tags.meta.notFoundTitle"),
          description: i18n.t("tags.meta.notFoundDescription")
        }]
      };
    }
    const {
      promotion,
      cover
    } = loaderData;
    if (!promotion || !cover) {
      return {
        meta: [{
          title: i18n.t("tags.meta.notFoundTitle"),
          description: i18n.t("tags.meta.notFoundDescription")
        }]
      };
    }
    const {
      title,
      count
    } = promotion;
    const coverImage = getImage(cover?.keyImages || [], ["OfferImageWide", "featuredMedia", "DieselGameBoxWide", "DieselStoreFrontWide"])?.url ?? "https://egdata.app/placeholder.webp";
    return {
      meta: [{
        title: i18n.t("tags.meta.title", {
          title
        })
      }, {
        name: "description",
        content: i18n.t("tags.meta.description", {
          count,
          title
        })
      }, {
        name: "og:title",
        content: i18n.t("tags.meta.socialTitle", {
          title
        })
      }, {
        name: "og:description",
        content: i18n.t("tags.meta.description", {
          count,
          title
        })
      }, {
        name: "twitter:title",
        content: i18n.t("tags.meta.socialTitle", {
          title
        })
      }, {
        name: "twitter:description",
        content: i18n.t("tags.meta.description", {
          count,
          title
        })
      }, {
        name: "og:image",
        content: coverImage
      }, {
        name: "twitter:image",
        content: coverImage
      }, {
        name: "twitter:card",
        content: "summary_large_image"
      }, {
        name: "og:type",
        content: "website"
      }, {
        name: "og:site_name",
        content: "egdata.app"
      }, {
        name: "og:url",
        content: `https://egdata.app/promotions/${params.id}`
      }
      //   {
      //     'script:ld+json': {
      //       '@context': 'https://schema.org',
      //       '@type': 'Event',
      //       name: title,
      //       description: `Checkout ${count} available offers for ${title} on egdata.app.`,
      //       image: coverImage,
      //       url: `https://egdata.app/promotions/${params.id}`,
      //       location: {
      //         url: `https://egdata.app/promotions/${params.id}`,
      //         name: title,
      //         image: coverImage,
      //       },
      //       organizer: {
      //         '@type': 'Organization',
      //         name: 'Epic Games',
      //         url: 'https://store.epicgames.com',
      //       },
      //       startDate:
      //         promotion.elements
      //           .find((game) =>
      //             game.price?.appliedRules.find((rule) => rule.startDate)
      //           )
      //           ?.price?.appliedRules.find((rule) => rule.startDate)?.startDate ??
      //         new Date(Date.now() - 86400000).toISOString(),
      //       offers: {
      //         '@type': 'AggregateOffer',
      //         availability: 'https://schema.org/InStock',
      //         priceCurrency:
      //           promotion.elements[0]?.price?.price.currencyCode ?? 'USD',
      //         lowPrice:
      //           Math.min(
      //             ...promotion.elements.map(
      //               (game) => game.price?.price.originalPrice ?? 0
      //             )
      //           ) / 100,
      //         highPrice:
      //           Math.max(
      //             ...promotion.elements.map(
      //               (game) => game.price?.price.originalPrice ?? 0
      //             )
      //           ) / 100,
      //         offerCount: count,
      //         offers: promotion.elements.map((game) => ({
      //           '@type': 'Offer',
      //           url: `https://egdata.app/offers/${game.id}`,
      //         })),
      //       },
      //     },
      //   },
      ]
    };
  }
});